import React, { useEffect, useState, useRef } from "react";
import { getAllPTHeaders, updatePTHeaderWithSlabs, addPTHeader, deletePTHeader } from "../../api/ptHeaderApi";
import { hasRight } from "../../utils/userRights";

export default function PTHeader() {
  const [headers, setHeaders] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    state: "",
    fromDate: "",
    toDate: "",
    slabDetails: [],
  });
  const [editId, setEditId] = useState(null);
  const [slabForm, setSlabForm] = useState({ fromAmount: "", toAmount: "", amount: "" });
  const formRef = useRef(null);

  const states = ["Maharashtra", "Madhya Pradesh", "Gujarat", "Karnataka", "Madhy Pradesh"];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const data = await getAllPTHeaders();
      setHeaders(data.data || data);
    } catch (err) {
      console.error("Error fetching PT Headers", err);
    }
  };

  const handleClear = () => {
    setFormData({ name: "", state: "", fromDate: "", toDate: "", slabDetails: [] });
    setSlabForm({ fromAmount: "", toAmount: "", amount: "" });
    setEditId(null);
  };

  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSlabChange = (e) => {
    const { name, value } = e.target;
    setSlabForm(prev => ({ ...prev, [name]: value }));
  };

  const addSlab = () => {
    if (!slabForm.fromAmount || !slabForm.toAmount || !slabForm.amount) {
      alert("Fill all slab fields before adding.");
      return;
    }

    const newSlab = {
      ptSlabID: 0,
      ptid: editId || 0,
      fromAmount: parseFloat(slabForm.fromAmount),
      toAmount: parseFloat(slabForm.toAmount),
      amount: parseFloat(slabForm.amount), // lowercase for input binding
    };

    setFormData(prev => ({
      ...prev,
      slabDetails: [...(prev.slabDetails || []), newSlab]
    }));

    setSlabForm({ fromAmount: "", toAmount: "", amount: "" });
  };

  const handleSlabEdit = (index, field, value) => {
    const updatedSlabs = [...formData.slabDetails];
    if (field === "amount") {
      updatedSlabs[index].amount = parseFloat(value);
    } else {
      updatedSlabs[index][field] = parseFloat(value);
    }
    updatedSlabs[index].ptid = editId || updatedSlabs[index].ptid || 0;
    setFormData(prev => ({ ...prev, slabDetails: updatedSlabs }));
  };

  const handleSlabDelete = (index) => {
    const updatedSlabs = [...formData.slabDetails];
    updatedSlabs.splice(index, 1);
    setFormData(prev => ({ ...prev, slabDetails: updatedSlabs }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.state) {
      alert("Name and State are required.");
      return;
    }

    if (!editId) {
      const duplicate = headers.some(
        (h) => h.state.toLowerCase() === formData.state.toLowerCase()
      );
      if (duplicate) {
        alert(`PT Header for state "${formData.state}" already exists 🚫`);
        return;
      }
    }
  
    // Map slabs to backend format (Amount with capital A)
    const slabsForBackend = formData.slabDetails.map(s => ({
      ptSlabID: s.ptSlabID || 0,
      ptid: editId || s.ptid || 0,
      fromAmount: s.fromAmount,
      toAmount: s.toAmount,
      Amount: s.amount ?? 0
    }));

    try {
      if (editId) {
        await updatePTHeaderWithSlabs({
          ...formData,
          ptid: editId,
          slabDetails: slabsForBackend,
          TrUser: "admin",
          TrDate: new Date().toISOString()
        });
        alert("PT Header updated successfully ✅");
      } else {
        await addPTHeader({
          ...formData,
          slabDetails: slabsForBackend,
          crUser: "admin",
          crDate: new Date().toISOString(),
          deleted: "N"
        });
        alert("PT Header created successfully 🎉");
      }

      // Reset form
      setFormData({ name: "", state: "", fromDate: "", toDate: "", slabDetails: [] });
      setEditId(null);
      setSlabForm({ fromAmount: "", toAmount: "", amount: "" });
      fetchData();
    } catch (err) {
      console.error("Failed to save PT Header", err);
      alert("Failed to save PT Header");
    }
  };

  const handleEdit = (item) => {
    const normalizedSlabs = (item.slabDetails || []).map(s => ({
      ...s,
      amount: s.Amount ?? s.amount ?? 0,
      fromAmount: s.fromAmount,
      toAmount: s.toAmount,
      ptSlabID: s.ptSlabID ?? 0,
      ptid: s.ptid ?? item.ptid
    }));

    setFormData({
      name: item.name,
      state: item.state,
      fromDate: item.fromDate?.split("T")[0],
      toDate: item.toDate?.split("T")[0],
      slabDetails: normalizedSlabs
    });

    setEditId(item.ptid);
    formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleDelete = async (ptid) => {
    if (window.confirm("Are you sure you want to delete this PT Header?")) {
      try {
        await deletePTHeader(ptid);
        alert("PT Header deleted successfully 🗑️");
        handleClear();
        // Optimistically update UI
        setHeaders((prev) => prev.filter((h) => h.ptid !== ptid));

        await fetchData();
      } catch (err) {
        console.error(err);
        alert("Failed to delete PT Header");
      }
    }
  };

  return (
  <div className="container mt-4 compact-table-container">
    <h4 className="compact-heading">PT Slab Details</h4>

    {/* Add / Edit Form */}
    <form ref={formRef} className="row g-2 mt-0 compact-form" onSubmit={handleSubmit}>
      <div className="col-md-3">
        <label className="form-label">Name <span className="text-danger">*</span></label>
        <input type="text" name="name" className="form-control" value={formData.name} onChange={handleHeaderChange} required />
      </div>

      <div className="col-md-3">
        <label className="form-label">State <span className="text-danger">*</span></label>
        <select name="state" className="form-select" value={formData.state} onChange={handleHeaderChange} required disabled={!!editId}>
          <option value="">-- Select State --</option>
          {states.map((s, i) => (
            <option key={i} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className="col-md-3">
        <label className="form-label">From Date</label>
        <input type="date" name="fromDate" className="form-control" value={formData.fromDate} onChange={handleHeaderChange} onKeyDown={(e) => e.preventDefault()} />
      </div>

      <div className="col-md-3">
        <label className="form-label">To Date</label>
        <input type="date" name="toDate" className="form-control" value={formData.toDate} onChange={handleHeaderChange} onKeyDown={(e) => e.preventDefault()} />
      </div>

      {/* Slab Inputs */}
      {!editId && (
        <>
          <div className="col-md-2">
            <input type="number" placeholder="From Amount" name="fromAmount" className="form-control" value={slabForm.fromAmount} onChange={handleSlabChange} />
          </div>
          <div className="col-md-2">
            <input type="number" placeholder="To Amount" name="toAmount" className="form-control" value={slabForm.toAmount} onChange={handleSlabChange} />
          </div>
          <div className="col-md-2">
            <input type="number" placeholder="PT Amount" name="amount" className="form-control" value={slabForm.amount} onChange={handleSlabChange} />
          </div>
          <div className="col-md-2">
            {/* <button type="button" className="btn btn-success" onClick={addSlab}>Add Slab</button> */}
            {/* {hasRight("PTHeaderMaster", "add") && (
              <button
                className="btn btn-success"
                onClick={addSlab}
              >
                Add Slab
              </button>
            )} */}
            {hasRight("PTHeaderMaster", "add") && (
              <button 
                type="button" 
                className="btn btn-success" 
                onClick={addSlab}
              >
                Add Slab
              </button>
            )}

          </div>
        </>
      )}

      {/* Slab Table */}
      {formData.slabDetails.length > 0 && (
        <div className="col-12">
          <table className="compact-table">
            <thead>
              <tr>
                <th>From Amount</th>
                <th>To Amount</th>
                <th>PT Amount</th>
                {!editId && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {formData.slabDetails.map((slab, idx) => (
                <tr key={idx}>
                  <td><input type="number" className="form-control" value={slab.fromAmount} onChange={(e) => handleSlabEdit(idx, "fromAmount", e.target.value)} /></td>
                  <td><input type="number" className="form-control" value={slab.toAmount} onChange={(e) => handleSlabEdit(idx, "toAmount", e.target.value)} /></td>
                  <td><input type="number" className="form-control" value={slab.amount} onChange={(e) => handleSlabEdit(idx, "amount", e.target.value)} /></td>
                  {!editId && (
                    <td>
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => handleSlabDelete(idx)}>Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="col-md-2">
        {/* <button type="submit" className="btn btn-success w-100">{editId ? "Update" : "Add"} PT Header</button> */}
        {(!editId && hasRight("PTHeaderMaster", "add")) ||
          (editId && hasRight("PTHeaderMaster", "update")) ? (
            <button type="submit" className="btn btn-success w-100">
              {editId ? "Update PT Header" : "Add PT Header"}
            </button>
          ) : null}

      </div>
      <div className="col-md-2">
        <button type="button" className="btn btn-secondary w-100" onClick={handleClear}>Clear</button>
      </div>
    </form>

    {/* PT Header Table */}
    <div className="compact-table-container mt-2">
      <table className="compact-table">
        <thead>
          <tr>
            <th>Sr</th>
            <th>Name</th>
            <th>State</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {headers.length > 0 ? headers.map((h, i) => (
            <tr key={h.ptid}>
              <td>{i + 1}</td>
              <td>{h.name}</td>
              <td>{h.state}</td>
              <td>
                {/* <button className="edit" onClick={() => handleEdit(h)}>Edit</button>
                <button className="delete" onClick={() => handleDelete(h.ptid)}>Delete</button> */}

                {/* Edit button → only show if user has Update right */}
                {hasRight("PTHeaderMaster", "update") && (
                  <button className="edit" onClick={() => handleEdit(h)}>
                    Edit
                  </button>
                )}
              
                {/* Delete button → only show if user has Delete right */}
                {hasRight("PTHeaderMaster", "delete") && (
                  <button
                    className="delete"
                    onClick={() => handleDelete(h.ptid)}
                  >
                    Delete
                  </button>
                )}
              </td>
            </tr>
          )) : (
            <tr>
              <td colSpan="4" className="text-center">No PT Headers found.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

}
